import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader";
import PageTitleBar from "../../../components/PageTitleBar";
import { Button } from "../../../components/Button";
import { selectCurrentUser, selectCurrentUserId } from "../../../store/models/User";
import {
  ModalTypes, UserRoleIds, Urls, UserRoleIds as UserRoles,
  QuotaUsageNameMap, Features, HideFilterLevel,
  VideoQuotaNameMap
} from "../../../constants";
import UIState from "../../../store/UIState";
import Organization, {
  selectCurrentOrg,
  selectCurrentOrgId,
  selectFeaturesEnabled,
  selectOrgQuota
} from "../../../store/models/Organization";
import { Tab, Tabs } from "../../../components/Tabs";
import Tooltip from "../../../components/Tooltip";
import { getVmResourcesString } from "../../../utils";
import { replaceTo } from "../../../store/history";
import _ from "lodash";
import { selectMobileLayout } from "../../../store/UIState";
import { selectCurrentProjectId } from "../../../store/models/Project";
import Ai, { selectAllNeuronVms, selectAllQuotas, selectAllVMs } from "../../../store/models/Ai";


const selector = createSelector(
  selectCurrentProjectId,
  selectCurrentUserId,
  selectCurrentUser,
  selectCurrentOrgId,
  selectCurrentOrg,
  (state, orgId) => selectOrgQuota(state, orgId ?? selectCurrentOrgId(state)),
  selectAllQuotas,
  selectFeaturesEnabled,
  selectAllVMs,
  selectAllNeuronVms,
  (currentProjectId, currentUserId, currentUser, currentOrgId,
    currentOrg, orgQuota, quotas, featuresEnabled, vms, neuronVms) => {
    return {
      currentProjectId,
      currentUserId,
      currentUser,
      currentOrgId,
      currentOrg,
      orgQuota,
      quotas,
      featuresEnabled,
      vms,
      neuronVms
    }
  }
)


const getQuotaTooltip = (quota) => {
  return (
    <div>
      {quota.description && <div className={"QuotaTooltip__Desc"}>{quota.description}</div>}
      <div
        className={"QuotaTooltip__Info"}>{getVmResourcesString(quota.resources)}</div>
    </div>
  )
}

export const QuotaPage = () => {

  const dispatch = useDispatch();
  const [selectedOrgId, setSelectedOrgId] = useState(null)

  const {
    currentProjectId,
    currentUserId,
    currentUser,
    currentOrgId,
    currentOrg,
    orgQuota,
    quotas,
    featuresEnabled,
    vms,
    neuronVms
  } = useSelector(state => selector(state, selectedOrgId));

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [upgradeError, setUpgradeError] = useState(null)
  const isMobile = useSelector(state => selectMobileLayout(state));
  const [upgradedPlan, setUpgradedPlan] = useState();
  const [upgrading, setUpgrading] = useState(false);
  const [isModalTriggered, setIsModalTriggered] = useState(false);
  const [usage, setUsage] = useState({});
  const [ondemandUsage, setOndemandUsage] = useState([]);
  const [selectedTab, setSelectedTab] = useState('vm');
  const [videoQuota, setVideoQuota] = useState({});
  const [quotaId, setQuotaId] = useState();

  useEffect(() => {
    fetchUserOrgs()
  }, []);

  useEffect(() => {
    dispatch(Ai.actions.fetchVMs(featuresEnabled[Features.VM_FULL_LIST]));
  }, [featuresEnabled[Features.VM_FULL_LIST]])

  useEffect(() => {
    if (quotas.length === 0) {
      dispatch(Ai.actions.fetchAllQuota());
    }
  }, [quotas])

  async function fetchQuotaId() {
    let quotaId = await dispatch(Organization.actions.fetchOrgQuotaId(selectedOrgId));
    setQuotaId(quotaId);
  }

  useEffect(() => {
    (async () => {
      if (!_.isEmpty(selectedOrgId)) {
        await fetchQuotaId();
      }
    })()
  }, [selectedOrgId])

  useEffect(() => {
    (async () => {
      if (!_.isEmpty(selectedOrgId)) {
        try {
          await fetchOrgQuota()
          setUsage({});
          let usageRes = await dispatch(Organization.actions.fetchOrgChatbotQuotaUsage(selectedOrgId));
          let volumeRes = await dispatch(Organization.actions.fetchOrgVolumeUsage(selectedOrgId))
          setUsage({
            chat_limit_10_min: _.get(usageRes, 'chat_limit_10_min'),
            chat_limit_daily: _.get(usageRes, 'chat_limit_daily'),
            embedding_token_limit_daily: _.get(usageRes, 'embedding_token_limit_daily'),
            persistent_storage: _.get(volumeRes, 'persistent_storage')
          })
          setVideoQuota({
            video_length_limit: _.get(usageRes, 'video_length_limit')
          })
          let onDemandRes = await dispatch(Organization.actions.fetchOrgOndemandQuotaUsage(selectedOrgId));
          const quotaMap = _.get(onDemandRes, 'model_quotas').reduce((acc, item) => {
            acc[item.template_id] = item;
            return acc;
          }, {})
          const ondemandQuota = _.get(onDemandRes, 'templates')
            .map(({ id, rank, name }) => ({
              name, rank, ...quotaMap[id]
            }))
            .sort((a, b) => b.rank - a.rank)
            .map(({ name, ...rest }) => ({
              name,
              ...rest
            }))
          setOndemandUsage(ondemandQuota);
        } catch (e) {
          console.log('Error in fetchData:', e.message);
        }
      }
    })();

  }, [selectedOrgId, quotaId]);

  const increasePlan = async () => {
    try {
      setUpgrading(true);
      let result = await dispatch(Organization.actions.upgradeOrgQuota(selectedOrgId));
      const { status, body } = result;
      if (status === 'success') {
        await fetchOrgQuota();
        setUpgradedPlan(_.get(body, 'quota_name'))
      } else {
        setUpgradedPlan();
      }
    } catch (e) {
      setUpgradeError(e.message);
      setUpgradedPlan();
    } finally {
      setUpgrading(false);
    }
  }

  useEffect(() => {
    if (!_.isEmpty(currentUser)) {
      if (currentUser.role !== UserRoles.ADMIN && currentUser?.role !== UserRoleIds.EDITOR)
        replaceTo(Urls.SETTINGS_PROFILE);
    }
  }, [currentUser])

  useEffect(() => {
    if (!_.isEmpty(currentOrgId)) {
      setSelectedOrgId(currentOrgId)
    }
  }, [currentOrgId]);

  const fetchUserOrgs = async () => {
    setLoading(true);
    setError(null);
    try {
      await dispatch(Organization.actions.fetchUserOrganizations(currentUserId))
    } catch (e) {
      setError(e.message)
    }
    setLoading(false);
  }

  const fetchOrgQuota = async () => {
    setLoading(true);
    setError(null);
    try {
      await dispatch(Organization.actions.fetchOrgQuota(selectedOrgId))
    } catch (e) {
      setError(e.message)
    }
    setLoading(false);
  }

  const onIncreaseQuota = async () => {
    if (upgradeError) setUpgradeError();
    await increasePlan();
    setIsModalTriggered(true);
  }

  useEffect(() => {
    if (isModalTriggered) {
      dispatch(UIState.actions.showModal(ModalTypes.INCREASE_QUOTA, {
        orgId: selectedOrgId,
        upgradedPlan: upgradedPlan,
        error: upgradeError,
        setUpgradedPlan: setUpgradedPlan,
        quotaId: quotaId,
        fetchQuotaId: fetchQuotaId
      }))

      setIsModalTriggered(false);
    }
  }, [isModalTriggered, upgradedPlan]);


  if (error)
    return (<div className={"ServicePage__Error"}>
      {error}
      <Button onClick={fetchOrgQuota()}
        color={"green-outline"}
        title={"Retry"} />
    </div>)

  return (<div className={"QuotaPage"}>
    <PageTitleBar alignTitleLeft={true} title={`${currentOrg.name} Quota`} />

    {loading
      ? <Loader />
      : <>
        <Button onClick={onIncreaseQuota}
          color={"green-outline"}
          title={"Increase quota"}
          loading={upgrading}
          disabled={quotaId === undefined}
        />

        <Tabs leftAlign={true}>
          <Tab isSelected={selectedTab === 'vm'} onClick={() => setSelectedTab('vm')}>
            Virtual Machines
          </Tab>
          <Tab isSelected={selectedTab === 'chatbot'} onClick={() => setSelectedTab('chatbot')}>
            Chatbot & Storage
          </Tab>
          <Tab isSelected={selectedTab === 'ondemand'} onClick={() => setSelectedTab('ondemand')}>
            On-demand model API
          </Tab>
          <Tab isSelected={selectedTab === 'video'} onClick={() => setSelectedTab('video')}>
            Video Services
          </Tab>
        </Tabs>

        {selectedTab === 'vm' && <table>
          <thead>
            <tr>
              <th>Machine Type</th>
              <th>Quota</th>
              <th>Amount Used</th>
              <th>Specs</th>
            </tr>
          </thead>
          <tbody>
            {_.map(orgQuota, (quota, key) => {
              let vm = [...Object.values(vms), ...Object.values(neuronVms)]
                .find(vm => vm.name === quota.name);
              return vm ? (<tr key={key}>
                <td><Tooltip tooltip={getQuotaTooltip(quota)}>{quota.name}</Tooltip></td>
                <td>{quota.quota ?? 0}</td>
                <td>{quota.usage ?? 0}</td>
                <td>{getVmResourcesString(quota.resources)}</td>
              </tr>) : <></>
            })}
          </tbody>
        </table>}


        {selectedTab === 'chatbot'
          && (quotaId === undefined
            ? <Loader />
            : Object.keys(usage).length > 0 && <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Quota</th>
                  <th>Amount Used</th>
                </tr>
              </thead>
              <tbody>
                {_.map(Object.keys(usage), (key, i) => {
                  const obj = usage[key];
                  return (<tr key={i}>
                    <td>{QuotaUsageNameMap[key]}</td>
                    <td>{obj.quota ?? 0}</td>
                    <td>{obj.usage ?? 0}</td>
                  </tr>)
                })}
              </tbody>
            </table>)}

        {selectedTab === 'ondemand'
          && (quotaId === undefined
            ? <Loader />
            : ondemandUsage.length > 0 && <table>
              <thead>
                <tr>
                  <th>Model name</th>
                  <th>Daily Quota</th>
                  <th>Daily Amount Used</th>
                  <th>10 Mins Quota</th>
                  <th>10 Mins Amount Used</th>
                </tr>
              </thead>
              <tbody>
                {_.map(ondemandUsage, (obj, i) => {
                  return (<tr key={i}>
                    <td>{obj.name}</td>
                    <td>{obj.daily_quota ?? 0}</td>
                    <td>{obj.daily_quota - obj.daily_remaining ?? 0}</td>
                    <td>{obj.ten_min_quota ?? 0}</td>
                    <td>{obj.ten_min_quota - obj.ten_min_remaining ?? 0}</td>
                  </tr>)
                })}
              </tbody>
            </table>)}

        {selectedTab === 'video' && <table>
          <thead>
            <tr>
              <th></th>
              <th>Quota</th>
            </tr>
          </thead>
          <tbody>
            {_.map(Object.keys(videoQuota), (key, i) => {
              return (<tr key={key}>
                <td>{VideoQuotaNameMap[key]}</td>
                <td>{videoQuota[key] ?? 0}</td>
              </tr>)
            })}
          </tbody>
        </table>}
      </>}
  </div>
  )
}
